import { useTiledImageSheetGenerationContext } from "@/components/common/tiled-image-sheet-generation-context";
import { changeMode } from "@/store/mode-slice";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { TiledImageSheetGenerationTask } from "@/utils/background-tasks";
import {
  FaroText,
  FontWeights,
  NoTranslate,
  useDialog,
} from "@faro-lotv/flat-ui";
import { isIElementGenericImgSheet } from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  createMutationDeleteElement,
  isBackgroundTaskActive,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, type JSX } from "react";
import { ErrorMessageWithDetails } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type TiledImageSheetGenerationTaskCardProps = {
  /** The task to track */
  task: TiledImageSheetGenerationTask;
};

/** @returns the upload menu card to track a tiled image sheet generation and upload progress */
export function TiledImageSheetGenerationTaskCard({
  task,
}: TiledImageSheetGenerationTaskCardProps): JSX.Element {
  const { getController } = useTiledImageSheetGenerationContext();
  const { createDialog } = useDialog();
  const apiClients = useApiClientContext();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const cancelImport = useCallback(async () => {
    const hasAccepted = await createDialog({
      title: "Cancel Import?",
      confirmText: "Cancel Import",
      cancelText: "Resume Import",
      content: (
        <FaroText variant="bodyM">
          Are you sure you want to cancel the import?
          <br />
          Your progress will be lost.
        </FaroText>
      ),
      variant: "danger",
    });
    const controller = hasAccepted && getController(task.id);
    if (controller) {
      controller.abort();
      if (task.iElementId) {
        const area = selectIElement(task.iElementId)(store.getState());
        const sheet = selectChildDepthFirst(
          area,
          isIElementGenericImgSheet,
        )(store.getState());

        if (!sheet) {
          // if area does not have any other sheet, we should delete the area while canceling the task of sheet upload
          const deleteMutation = createMutationDeleteElement(
            task.iElementId,
            true,
          );
          // Delete the empty area from the project.
          await apiClients.projectApiClient.applyMutations([deleteMutation]);

          // after canceling the task, we should switch to the import area mode
          // to return user to starting point of the workflow he's canceled
          dispatch(changeMode("importSheet"));
        }
      }
    }
  }, [
    createDialog,
    getController,
    task.id,
    task.iElementId,
    store,
    apiClients.projectApiClient,
    dispatch,
  ]);

  const actions: [CardAction] | undefined =
    task.state === BackgroundTaskState.started ||
    task.state === BackgroundTaskState.created
      ? [
          {
            name: "Cancel import",
            callback: () => cancelImport(),
            color: "error",
          },
        ]
      : undefined;

  return (
    <GenericCardLayout
      name={<NoTranslate>{task.metadata.sheetName}</NoTranslate>}
      subText={getTaskTypeDescription(task)}
      startTime={task.createdAt}
      aborted={task.state === BackgroundTaskState.aborted}
      menu={actions}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress
          label={getTaskStatusLabel(task)}
          subLabel={
            task.progress > 0 && (
              <NoTranslate>{Math.round(task.progress)}%</NoTranslate>
            )
          }
          task={task}
        />
      )}
      {task.state === BackgroundTaskState.failed && (
        <ErrorMessageWithDetails
          message={
            <FaroText variant="inlineError" fontWeight={FontWeights.Bold}>
              Tiled Image Sheet Generation failed.
            </FaroText>
          }
          errorDetails={
            <FaroText variant="helpText">{task.devMessage}</FaroText>
          }
        />
      )}
    </GenericCardLayout>
  );
}
